import React, { useEffect, useState } from "react";
import Icon1 from "../icons/Icon";
import SecondaryButton from "../buttons/SecondaryButton.js";
import extractXml from "../api/xml/extractXml";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken.js";
import PrimaryButton from "../buttons/PrimaryButton.js";
import generatePDF from "../api/xml/generatePDF.js";
import extractXmlBoth from "../api/xml/extractXmlBoth.js";
import generateBoth from "../api/xml/generateBoth.js";
import Space from "../layout/Space.js";
import CheckBoxGrey from "../textfields/CheckBoxGrey.js";
import { useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import SmallSpinner from "../loading/SmallSpinner.js";
import JSZip from "jszip";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi.js";
import { addActivityLog } from "../config/OnlineStatus.js";
import { getDocDataById, getRefFromId } from "../models/DataFunctions.js";
import { generateComplexModelAttestation } from "./xml_extract_helpers.js";

const Xml = ({
  document,
  folder,
  orgData,
  userData,
  onClose,
  prefix,
  onFetch,
  modif_thermal_xml = false,
}) => {
  const [step, setStep] = useState("choice");

  const [allfields, setAllFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [url, setUrl] = useState("");
  const [urlInsulator, setUrlInsulator] = useState("");
  const [loadingData, setLoadingData] = useState(null);
  const [signCoords, setSignCoords] = useState({ carbone: [], isolant: [] });
  const new_model = orgData.config?.ath?.new_model || false;
  useEffect(() => {
    async function updateSignCoords() {
      if (
        signCoords?.carbone?.length === 0 &&
        signCoords?.isolant?.length === 0
      )
        return;
      await updateDocDataFieldApi(
        folder.ref,
        "last_file_extracted_sign_coords",
        signCoords,
        prefix
      );
    }
    updateSignCoords();
  }, [signCoords]);

  const extractIsolant = async () => {
    setLoadingData("insulator");
    const url = modif_thermal_xml
      ? folder.modif_thermal.xml.file.url
      : folder.xml.file.url;
    const token = await getOrRefreshFirebaseToken();
    const orgId = orgData.id;
    const folderId = folder.id;

    const res = await extractXml(
      url,
      "insulator",
      token,
      orgId,
      folderId,
      orgData.infos.phone,
      orgData.id,
      orgData.infos.email,
      orgData.infos.adresse,
      orgData.name,
      prefix,
      orgData,
      new_model,
      modif_thermal_xml
    );
    await addActivityLog(
      "Attestation isolant générée",
      folder.ref,
      orgData.id,
      userData.id,
      "attestation_generated",
      prefix
    );

    setUrl(res.url);
    await updateDocDataFieldApi(
      folder.ref,
      "last_file_extracted_sign_coords",
      res.sign_coords,
      prefix
    );

    setStep("success");
    onFetch();
    handleClickVariant("Attestation Isolant générée avec succès", "success");
    setLoadingData(null);
  };

  const preSelectFields = (fields) => {
    if (orgData.config?.ath?.preselect) {
      // preselect all fields with the first sous lot number of each sous lot "range", for example select 1.1, 2.1, 3.1, etc.
      const taken_slots = [];
      const preselectedFields = [];
      for (const field of fields) {
        if (field.num.endsWith(".1") && !taken_slots.includes(field.num)) {
          preselectedFields.push(field.X);
          taken_slots.push(field.num);
        }
      }

      // set the selected fields to the unique preselected fields
      // convert to string
      setSelectedFields(preselectedFields);
    } else {
      // preselect all fields
    }
  };

  const extractCarbon = async () => {
    setLoadingData("carbon");
    const url = modif_thermal_xml
      ? folder.modif_thermal.xml.file.url
      : folder.xml.file.url;
    const token = await getOrRefreshFirebaseToken();
    const orgId = orgData.id;
    const folderId = folder.id;

    const res = await extractXml(
      url,
      "carbon",
      token,
      orgId,
      folderId,
      orgData.infos.phone,
      orgData.id,
      orgData.infos.email,
      orgData.infos.adresse,
      orgData.name,
      prefix,
      orgData,

      new_model,
      modif_thermal_xml
    );

    let allfields = res.list;

    setAllFields(allfields);

    let selectedFieldsId = orgData.config?.ath?.default_carbon_fields;

    let selectedFields = [];
    if (selectedFieldsId) {
      for (let i = 0; i < selectedFieldsId.length; i++) {
        selectedFields[i] = (
          res.list.findIndex((field) => field.id === selectedFieldsId[i]) + 1
        ).toString();
      }
      setSelectedFields(selectedFields);
    }

    setStep("carbon");
    setLoadingData(null);
    preSelectFields(allfields);
  };

  const extractBoth = async () => {
    setLoadingData("both");

    const url = modif_thermal_xml
      ? folder.modif_thermal.xml.file.url
      : folder.xml.file.url;
    const token = await getOrRefreshFirebaseToken();
    const res = await extractXmlBoth(
      url,
      token,
      orgData,
      orgData.infos.phone,
      folder.id,
      orgData.infos.email,
      orgData.infos.adresse,
      orgData.name,
      orgData.id,
      prefix,
      new_model
    );
    setAllFields(res.list);

    let selectedFieldsId = orgData.config?.ath?.default_carbon_fields;

    let selectedFields = [];
    if (selectedFieldsId) {
      for (let i = 0; i < selectedFieldsId.length; i++) {
        selectedFields[i] = (
          res.list.findIndex((field) => field.id === selectedFieldsId[i]) + 1
        ).toString();
      }
      setSelectedFields(selectedFields);
    }

    setUrlInsulator(res.urlInsulator);
    setSignCoords((prevSignCoords) => {
      return {
        isolant: res.sign_coords.isolant,
        carbone: prevSignCoords.carbone,
      };
    });

    setStep("both");
    setLoadingData(null);
    preSelectFields(res.list);
  };

  const generateCarbone = async () => {
    setLoadingData("generate");
    if (step === "both") {
      await generateBoth2(); //generates carbon and merges carbon with insulator
      return;
    }
    const token = await getOrRefreshFirebaseToken();
    const url = modif_thermal_xml
      ? folder.modif_thermal.xml.file.url
      : folder.xml.file.url;
    const res = await generatePDF(
      url,
      selectedFields,
      "carbon",
      orgData.id,
      token,
      orgData,
      folder.id,
      orgData.infos.phone,
      orgData.infos.email,
      orgData.infos.adresse,
      orgData.name,
      prefix,
      new_model
    );
    setSignCoords((prevSignCoords) => {
      return {
        carbone: res.sign_coords.carbone,
        isolant: prevSignCoords.isolant,
      };
    });
    await addActivityLog(
      "Attestation carbone générée",
      folder.ref,
      orgData.id,
      userData.id,
      "attestation_generated",
      prefix
    );
    await updateDocDataFieldApi(
      folder.ref,
      "last_file_extracted_sign_coords",
      res.sign_coords,
      prefix
    );
    handleClickVariant("Attestation Carbone générée avec succès", "success");
    onFetch();
    setLoadingData(null);
  };

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const handleCheck = (field) => {
    if (selectedFields.includes(field.X)) {
      setSelectedFields(selectedFields.filter((f) => f !== field.X));
    } else {
      setSelectedFields([...selectedFields, field.X]);
    }
  };

  const theme = useTheme();
  const [extractingData, setExtractingData] = useState(false);

  const handleComplexModelAttestation = async (type) => {
    setExtractingData(true);
    setLoadingData(type);

    try {
      let xml_file = modif_thermal_xml ? folder.modif_thermal.xml : folder.xml;
      let client = {};
      if (folder.client_id) {
        client = await getDocDataById("org", folder.client_id);
      } else {
        client = {
          name: folder.nom,
          address: folder.reglement_infos?.address || "use_operation_data",
          postal: folder.reglement_infos?.postal || "use_operation_data",
          city: folder.reglement_infos?.city || "use_operation_data",
        };
      }
      let res = await generateComplexModelAttestation(
        xml_file,
        type,
        folder,
        client,
        orgData,
        modif_thermal_xml ? true : false,
        prefix
      );
      setExtractingData(false);

      await addActivityLog(
        "Attestation isolant générée",
        folder.ref,
        orgData.id,
        userData.id,
        "attestation_generated",
        prefix
      );

      setUrl(res.url);
      // await updateDocDataFieldApi(
      //   folder.ref,
      //   "last_file_extracted_sign_coords",
      //   res.sign_coords,
      //   prefix
      // );

      setStep("success");
      onFetch();
      handleClickVariant("Attestation Isolant générée avec succès", "success");
      setLoadingData(null);
    } catch (error) {
      console.error(
        "Erreur lors de la génération de l'attestation complexe",
        error
      );
      handleClickVariant(
        "Erreur lors de la génération de l'attestation complexe",
        "error"
      );
      setExtractingData(false);
      return;
    }
  };

  const handleSignCoords = (coords) => {
    setSignCoords((prevSignCoords) => {
      return {
        carbone: coords.carbone,
        isolant: prevSignCoords.isolant,
      };
    });
  };
  const generateBoth2 = async () => {
    const token = await getOrRefreshFirebaseToken();
    const url = modif_thermal_xml
      ? folder.modif_thermal.xml.file.url
      : folder.xml.file.url;
    const res = await generateBoth(
      url,
      orgData.id,
      selectedFields,
      urlInsulator,
      token,
      orgData.ref,
      orgData.infos.phone,
      folder.id,
      orgData.infos.email,
      orgData.infos.adresse,
      orgData.name,
      prefix,
      handleSignCoords,
      orgData,
      new_model
    );
    await addActivityLog(
      "Attestation carbone et isolants générées",
      folder.ref,
      orgData.id,
      userData.id,
      "attestation_generated",
      prefix
    );
    handleClickVariant("Attestations générées avec succès", "success");
    onFetch();
  };

  return (
    <div className="flex flex-col gap-2 bg-neutral-50 p-4 rounded-xl">
      {step === "choice" && (
        <>
          {orgData.config?.ath?.model === "complex" ? (
            <>
              <SecondaryButton
                onClick={() => handleComplexModelAttestation("insulator")}
                disabled={loadingData ? true : false}
              >
                Générer Attestation Isolant
                <SmallSpinner loading={loadingData === "insulator"} />
              </SecondaryButton>
              <SecondaryButton
                onClick={() => handleComplexModelAttestation("carbon")}
                disabled={loadingData ? true : false}
              >
                Générer Attestation Carbone
                <SmallSpinner loading={loadingData === "carbon"} />
              </SecondaryButton>
              <PrimaryButton
                onClick={() =>
                  handleComplexModelAttestation("insulator_carbon")
                }
                disabled={loadingData ? true : false}
              >
                Générer Attestations Isolant et Carbone
                <SmallSpinner loading={loadingData === "both"} />
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton
                onClick={extractIsolant}
                disabled={loadingData ? true : false}
              >
                Générer Attestation Isolant
                <SmallSpinner loading={loadingData === "insulator"} />
              </SecondaryButton>
              <SecondaryButton
                onClick={extractCarbon}
                disabled={loadingData ? true : false}
              >
                Générer Attestation Carbone
                <SmallSpinner loading={loadingData === "carbon"} />
              </SecondaryButton>
              <PrimaryButton
                onClick={extractBoth}
                disabled={loadingData ? true : false}
              >
                Générer Attestations Isolant et Carbone
                <SmallSpinner loading={loadingData === "both"} />
              </PrimaryButton>
            </>
          )}
        </>
      )}

      {(step == "carbon" || step == "both") && (
        <div className="flex flex-col items-center overflow-auto w-full max-h-c500">
          <p>Choisir les champs</p>
          <div className="flex flex-col w-full">
            <div key="sdsds" className="flex flex-row gap-2">
              <Space x={5}></Space>
              <p className="text-h3 w-c100">Sous Lot</p>
              <p className="text-h3  w-c500 ">Nom</p>
              <p className="text-h3  w-c100">ID</p>
              <p className="text-h3  w-c50">Qt</p>
              <p className="text-h3  w-c50">Un</p>
            </div>
            {allfields.map((field) => (
              <div
                key={field.X}
                className={`flex flex-row gap-2 w-full rounded-lg px-1 items-center ${
                  selectedFields.includes(field.X) ? "bg-blue-50" : ""
                }`}
              >
                <CheckBoxGrey
                  checked={selectedFields.includes(field.X)}
                  onChange={() => handleCheck(field)}
                ></CheckBoxGrey>

                <span className="text-h3 w-c100">sous lot {field.num}</span>
                <span className="text-h3  w-c450 ">
                  {field.nom?.substring(0, 65)}
                </span>
                <span className="text-h3  w-c100">
                  {field.id?.substring(0, 12)}
                </span>
                <span className="text-h3  w-c50">{field.qt}</span>
                <span className="text-h3  w-c50">{field.un}</span>
              </div>
            ))}
          </div>
          <Space y={15}></Space>
          <div className="flex flex-row w-full justify-end p-3 gap-2">
            <SecondaryButton onClick={onClose}>Annuler</SecondaryButton>
            <PrimaryButton
              onClick={generateCarbone}
              disabled={loadingData ? true : false}
            >
              Générer l'attestation{" "}
              <SmallSpinner loading={loadingData === "generate"} />
            </PrimaryButton>
          </div>
          <Space y={15}></Space>
        </div>
      )}

      {step === "success" && (
        <div className="flex flex-col items-center">
          <Icon1 name="check" className="text-green-500" />
          <p>Attestation Isolant générée avec succès</p>
          <SecondaryButton onClick={onClose}>Fermer</SecondaryButton>
        </div>
      )}
    </div>
  );
};

export default Xml;
